import React, { useRef, useEffect, useState, useCallback, memo } from 'react';

import PropTypes from 'prop-types';
import { produce } from 'immer';
import _ from 'lodash';
import useTheme from '@hooks/useTheme';

import PlotLineTip from './plotLineTip';

import {
  xAxisStyleDefault,
  yAxisStyleDefault,
  defaultToolTip,
  defaultAxis,
  defaultLegendStyle,
} from '../config';

import Chart from './lineChart';
import D_Number from '@utils/number';
import D_Currency from '@utils/currency';

const avgLegendNameFix = '5yr Avg';
const xAxixSuffix = 'FY';
const currencyISOCode = {
  current: 'USD',
};
const colors = {
  BUYER: '#FFB600',
  TARGET: '#0081E9',
};
const plotLineId = {
  BUYER: 0,
  TARGET: 1,
};
const defaultAvgLegendSeries = {
  dashStyle: 'shortdash',
  data: [],
  marker: {
    enabled: false,
  },
};

const buyerPlotLine = {
  id: plotLineId.BUYER,
  color: colors.BUYER,
  dashStyle: 'longdash',
  value: null,
  width: 2,
};
const targetPlotLine = {
  id: plotLineId.TARGET,
  color: colors.TARGET,
  dashStyle: 'longdash',
  value: null,
  width: 2,
};

const plotMap = [buyerPlotLine, targetPlotLine];

const currencyFormat = (number, currency) => {
  currency = currency || currencyISOCode.current;
  if (!currency) {
    return D_Number.format(number);
  }

  return D_Currency.format({
    value: number,
    currencyIsoCode: currency,
  });
};

const tooltipFormatter = (currency, isLightMode) =>
  function () {
    return (
      `<b style="color: ${isLightMode ? '#000000' : '#DBDBDB'}">${
        this.category
      }</b>` +
      '<br/>' +
      `<b style="color:${isLightMode ? '#000000' : '#DBDBDB'}">${
        this.series.options.name
      }</b>` +
      `: <span>${currencyFormat(this.y, currency)}</span>`
    );
  };

const getXAxisCate = (...args) => {
  try {
    let xAxisTemp = args.reduce((result, argsItem) => {
      if (!Array.isArray(argsItem)) {
        return result;
      }

      const data = _.compact(argsItem).map((item) => {
        const { displayEndDate = '' } = item[0] || {};
        return Number(displayEndDate.replace(/[^0-9]/g, ''));
      });

      result = [...result, ...data];

      return result;
    }, []);

    xAxisTemp = Array.from(new Set(xAxisTemp))
      .sort((a, b) => a - b)
      .map((axis) => `${xAxixSuffix}${axis}`);

    return xAxisTemp;
  } catch (error) {
    console.log(error);
    return [];
  }
};

const defaultOptions = {
  chart: {
    type: 'line',
    style: {
      'font-family': ' HelveticaNeue-Medium,Arial',
    },
  },
  title: {
    text: null,
  },
  credits: {
    enabled: false,
  },
  legend: {
    // itemDistance: 12,
    // x: 16,
    itemStyle: defaultLegendStyle,
  },

  xAxis: {
    ...defaultAxis,
    categories: [],
    labels: {
      style: xAxisStyleDefault,
    },
  },
  yAxis: {
    ...defaultAxis,
    tickAmount: 5,
    labels: {
      style: yAxisStyleDefault,
      formatter() {
        return currencyFormat(this.value);
      },
    },
    plotLines: [],
  },
  tooltip: {
    ...defaultToolTip,
    crosshairs: [{ width: 1, color: '#666' }],
    split: true,
  },
  series: [
    {
      name: 'Tokyo',
      color: colors.BUYER,
      marker: {
        fill: colors.BUYER,
        radius: 1,
      },
      data: [],
    },
    {
      name: 'London',
      color: colors.TARGET,
      marker: {
        fill: colors.TARGET,
        radius: 1,
      },
      data: [],
    },
    {
      ...defaultAvgLegendSeries,
      name: 'buyer 5yr Avg',
      color: colors.BUYER,
    },
    {
      ...defaultAvgLegendSeries,
      name: 'target 5yr Avg',
      color: colors.TARGET,
    },
  ],
};

function EpsChart({ data }) {
  const chartRef = useRef(null);
  const tipRef = useRef(null);
  const [options, setOptions] = useState(defaultOptions);
  const { theme } = useTheme();
  const isLightMode = theme === 'light';

  const handleMouseOver = (e, value) => {
    const { clientX, clientY } = e || {};

    if (clientX && clientY && tipRef.current) {
      tipRef.current.show(
        { metric: 'Avg', num: value },
        {
          x: clientX,
          y: clientY,
        },
      );
    }
  };

  const handleMouseOut = () => {
    tipRef.current && tipRef.current.hide();
  };

  const handleFakeLegendClick = useCallback(
    (e, id) => {
      const visible = e.target.visible;
      let newOpts;

      if (visible) {
        newOpts = produce(options, (draft) => {
          const plotMapTemp = options.yAxis.plotLines.filter(
            (item) => item && item.id !== id,
          );

          draft.yAxis.plotLines = plotMapTemp;
        });
      } else {
        newOpts = produce(options, (draft) => {
          const plotMapTemp = [];

          for (const item of options.yAxis.plotLines) {
            if (item && item.id !== undefined) {
              plotMapTemp[item.id] = item;
            }
          }
          plotMapTemp[id] = plotMap[id];

          draft.yAxis.plotLines = plotMapTemp;
        });
      }

      newOpts && setOptions(newOpts);
    },
    [options],
  );
  //initialize option
  useEffect(() => {
    const newOpts = produce(options, (draft) => {
      const {
        buyer: {
          companyName: bName,
          eps: bData = [],
          avg5Yr: bAvg,
          currencyISOCode: bCurrencyISOCode,
        } = {},
        target: {
          companyName: tName,
          eps: tData = [],
          avg5Yr: tAvg,
          currencyISOCode: tCurrencyISOCode,
        } = {},
      } = data;
      currencyISOCode.current =
        bCurrencyISOCode === tCurrencyISOCode ? bCurrencyISOCode : '';

      const xAxisCate = getXAxisCate(bData, tData);
      draft.xAxis.categories = xAxisCate;
      // initialize plotline:[buyer,target]
      const plotLine = [
        {
          ...buyerPlotLine,
          value: bAvg,
          events: {
            mouseover: (e) =>
              handleMouseOver(
                e,
                D_Currency.format({
                  value: bAvg,
                  currencyIsoCode: bCurrencyISOCode,
                }),
              ),
            mouseout: handleMouseOut,
          },
        },
        {
          ...targetPlotLine,
          value: tAvg,
          events: {
            mouseover: (e) =>
              handleMouseOver(
                e,
                D_Currency.format({
                  value: tAvg,
                  currencyIsoCode: tCurrencyISOCode,
                }),
              ),
            mouseout: handleMouseOut,
          },
        },
      ];
      plotMap[0] = plotLine[0];
      plotMap[1] = plotLine[1];

      draft.yAxis.plotLines = plotLine;
      draft.plotOptions = {
        line: {
          marker: {
            symbol: 'circle',
          },
        },
      };

      const dataTemp = [
        ...tData.map((item) => {
          if (Array.isArray(item)) {
            return item[1];
          }

          return null;
        }),
        ...bData.map((item) => {
          if (Array.isArray(item)) {
            return item[1];
          }

          return null;
        }),
      ];

      //set yAxis's extreme
      const maxData = Math.max(...dataTemp);
      draft.yAxis.max = maxData + Math.abs(Math.floor(maxData * 0.2)) || null;

      if (Math.abs(bAvg - tAvg) > 500) {
        draft.yAxis.type = 'logarithmic';
      }

      //sync avg plotline to legend
      /**
       * series:[buyer,target,buyer avg legend,target avg legend]
       */
      draft.series[2] = {
        ...draft.series[2],
        name: `${bName} ${avgLegendNameFix}`,
        events: {
          legendItemClick(e) {
            handleFakeLegendClick(e, plotLineId.BUYER);
          },
        },
      };

      draft.series[3] = {
        ...draft.series[3],
        name: `${tName} ${avgLegendNameFix}`,
        events: {
          legendItemClick(e) {
            handleFakeLegendClick(e, plotLineId.TARGET);
          },
        },
      };

      //set series data
      draft.series[0] = {
        ...draft.series[0],
        name: bName,
        data: bData,
        tooltip: {
          pointFormatter() {
            return tooltipFormatter(bCurrencyISOCode, isLightMode).call(this);
          },
        },
      };

      draft.series[1] = {
        ...draft.series[1],
        name: tName,
        data: tData,
        tooltip: {
          pointFormatter() {
            return tooltipFormatter(tCurrencyISOCode, isLightMode).call(this);
          },
        },
      };

      return draft;
    });
    if (
      JSON.stringify(options) !== JSON.stringify(newOpts)
      // options.series[0].data.length !== newOpts.series[0].data.length ||
      // options.series[1].data.length !== newOpts.series[1].data.length ||
      // options.series[0].name !== newOpts.series[0].name ||
      // options.series[1].name !== newOpts.series[1].name
    ) {
      setOptions(newOpts);
    }
  }, [data, options, isLightMode, handleFakeLegendClick]);

  console.log('options: ', options);
  return (
    <>
      <PlotLineTip ref={tipRef} />
      <Chart ref={chartRef} options={options} />
    </>
  );
}

EpsChart.propTypes = {
  data: PropTypes.object.isRequired,
};

export default memo(EpsChart);
